import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { MenuAPI, GalleryAPI, EventsAPI, CategoriesAPI, UsersAPI } from "@/admin/api";
import { useAdmin } from "@/admin/store";

const Stat = ({ value, label }) => (
  <div className="border border-cream/12 px-7 py-8">
    <p className="font-display text-4xl text-brass">{value}</p>
    <p className="mt-2 font-body text-[10px] tracking-[0.3em] uppercase text-cream/55">{label}</p>
  </div>
);

const Dashboard = () => {
  const { activity } = useAdmin();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const { data: menuItems = [] } = useQuery({ queryKey: ["menuItems"], queryFn: MenuAPI.getAll });
  const { data: gallery = [] } = useQuery({ queryKey: ["gallery"], queryFn: GalleryAPI.getAll });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: EventsAPI.getAll });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: CategoriesAPI.getAll });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: UsersAPI.getAll });

  const stats = [
    { value: menuItems.filter((m) => !m.isDeleted).length, label: "Menu Items" },
    { value: gallery.filter((g) => !g.isDeleted).length, label: "Gallery Images" },
    { value: events.filter((e) => {
        if (e.isDeleted) return false;
        if (!e.eventDate) return true;
        const dateStr = e.eventDate.includes("T") ? e.eventDate.split("T")[0] : e.eventDate;
        return dateStr >= today;
      }).length, label: "Upcoming Events" },
    { value: categories.filter((c) => !c.isDeleted).length, label: "Categories" },
    { value: users.filter((u) => !u.isDeleted).length, label: "Users" },
  ];

  const actions = [
    { label: "+ Add Menu Item", to: "/admin/menu/new", testId: "qa-add-menu-item" },
    { label: "+ Add Gallery Image", to: "/admin/gallery", state: { add: true }, testId: "qa-add-gallery" },
    { label: "+ Add Event", to: "/admin/events", state: { add: true }, testId: "qa-add-event" },
    { label: "+ Add Category", to: "/admin/categories", state: { add: true }, testId: "qa-add-category" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl tracking-tight text-ivory sm:text-4xl">
        GOOD EVENING, <em className="text-brass">ADMIN.</em>
      </h1>
      <p className="mt-2 font-body text-sm font-light text-cream/55">
        A quiet look at what is happening inside the Veedu.
      </p>

      <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {stats.map((s) => (
          <Stat key={s.label} {...s} />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {actions.map((a) => (
          <button
            key={a.label}
            data-testid={a.testId}
            onClick={() => navigate(a.to, a.state ? { state: a.state } : undefined)}
            className="flex items-center gap-2 border border-cream/15 px-5 py-3 font-body text-[10px] tracking-[0.3em] uppercase text-cream/70 transition-all duration-300 hover:border-brass/60 hover:text-brass"
          >
            <Plus size={12} strokeWidth={1.5} /> {a.label.replace("+ ", "")}
          </button>
        ))}
      </div>

      <div className="mt-16">
        <h2 className="font-body text-[10px] tracking-[0.4em] uppercase text-cream/45">
          Recent activity
        </h2>
        {activity.length > 0 ? (
          <div className="mt-6 border-l border-cream/12">
            {activity.map((a, i) => (
              <div key={i} className="relative pb-7 pl-8" data-testid={`activity-${i}`}>
                <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full border border-brass/60 bg-ink" />
                <p className="font-body text-sm font-light text-cream/80">
                  {a.action} — <span className="text-cream">{a.target}</span>
                </p>
                <p className="mt-1 font-body text-[10px] tracking-[0.25em] uppercase text-cream/40">
                  {a.when}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 font-body text-xs text-cream/40 italic">
            No recent activity recorded this session.
          </p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
